import _classCallCheck from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/createClass.js";
import "core-js/modules/esnext.global-this.js";
import { EventBoundary } from './EventBoundary.mjs';
import { FederatedPointerEvent } from './FederatedPointerEvent.mjs';
import { FederatedWheelEvent } from './FederatedWheelEvent.mjs';
import { ExtensionType, extensions } from '@pixi/core';
var MOUSE_POINTER_ID = 1;
var TOUCH_TO_POINTER = {
  touchstart: "pointerdown",
  touchend: "pointerup",
  touchendoutside: "pointerupoutside",
  touchmove: "pointermove",
  touchcancel: "pointercancel"
};
var EventSystem = /*#__PURE__*/function () {
  function EventSystem(renderer) {
    _classCallCheck(this, EventSystem);
    this.supportsTouchEvents = "ontouchstart" in globalThis;
    this.supportsPointerEvents = !!globalThis.PointerEvent;
    this.domElement = null;
    this.resolution = 1;
    this.renderer = renderer;
    this.rootBoundary = new EventBoundary(null);
    this.autoPreventDefault = true;
    this.eventsAdded = false;
    this.rootPointerEvent = new FederatedPointerEvent(null);
    this.rootWheelEvent = new FederatedWheelEvent(null);
    this.cursorStyles = {
      default: "inherit",
      pointer: "pointer"
    };
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerOverOut = this.onPointerOverOut.bind(this);
    this.onWheel = this.onWheel.bind(this);
  }
  _createClass(EventSystem, [{
    key: "init",
    value: function init() {
      var _this$renderer = this.renderer,
        view = _this$renderer.view,
        resolution = _this$renderer.resolution;
      this.setTargetElement(view);
      this.resolution = resolution;
    }
  }, {
    key: "resolutionChange",
    value: function resolutionChange(resolution) {
      this.resolution = resolution;
    }
  }, {
    key: "destroy",
    value: function destroy() {
      this.setTargetElement(null);
      this.renderer = null;
    }
  }, {
    key: "setCursor",
    value: function setCursor(mode) {
      mode = mode || "default";
      var applyStyles = true;
      if (globalThis.OffscreenCanvas && this.domElement instanceof OffscreenCanvas) {
        applyStyles = false;
      }
      if (this.currentCursor === mode) {
        return;
      }
      this.currentCursor = mode;
      var style = this.cursorStyles[mode];
      if (style) {
        switch (typeof style) {
          case "string":
            if (applyStyles) {
              this.domElement.style.cursor = style;
            }
            break;
          case "function":
            style(mode);
            break;
          case "object":
            if (applyStyles) {
              Object.assign(this.domElement.style, style);
            }
            break;
        }
      } else if (applyStyles && typeof mode === "string" && !Object.prototype.hasOwnProperty.call(this.cursorStyles, mode)) {
        this.domElement.style.cursor = mode;
      }
    }
  }, {
    key: "onPointerDown",
    value: function onPointerDown(nativeEvent) {
      this.rootBoundary.rootTarget = this.renderer.lastObjectRendered;
      if (this.supportsTouchEvents && nativeEvent.pointerType === "touch") return;
      var events = this.normalizeToPointerData(nativeEvent);
      if (this.autoPreventDefault && events[0].isNormalized) {
        var cancelable = nativeEvent.cancelable || !("cancelable" in nativeEvent);
        if (cancelable) {
          nativeEvent.preventDefault();
        }
      }
      for (var i = 0, j = events.length; i < j; i++) {
        var nativeEvent2 = events[i];
        var federatedEvent = this.bootstrapEvent(this.rootPointerEvent, nativeEvent2);
        this.rootBoundary.mapEvent(federatedEvent);
      }
      this.setCursor(this.rootBoundary.cursor);
    }
  }, {
    key: "onPointerMove",
    value: function onPointerMove(nativeEvent) {
      this.rootBoundary.rootTarget = this.renderer.lastObjectRendered;
      if (this.supportsTouchEvents && nativeEvent.pointerType === "touch") return;
      var normalizedEvents = this.normalizeToPointerData(nativeEvent);
      for (var i = 0, j = normalizedEvents.length; i < j; i++) {
        var event = this.bootstrapEvent(this.rootPointerEvent, normalizedEvents[i]);
        this.rootBoundary.mapEvent(event);
      }
      this.setCursor(this.rootBoundary.cursor);
    }
  }, {
    key: "onPointerUp",
    value: function onPointerUp(nativeEvent) {
      this.rootBoundary.rootTarget = this.renderer.lastObjectRendered;
      if (this.supportsTouchEvents && nativeEvent.pointerType === "touch") return;
      var target = nativeEvent.target;
      if (nativeEvent.composedPath && nativeEvent.composedPath().length > 0) {
        target = nativeEvent.composedPath()[0];
      }
      var outside = target !== this.domElement ? "outside" : "";
      var normalizedEvents = this.normalizeToPointerData(nativeEvent);
      for (var i = 0, j = normalizedEvents.length; i < j; i++) {
        var event = this.bootstrapEvent(this.rootPointerEvent, normalizedEvents[i]);
        event.type += outside;
        this.rootBoundary.mapEvent(event);
      }
      this.setCursor(this.rootBoundary.cursor);
    }
  }, {
    key: "onPointerOverOut",
    value: function onPointerOverOut(nativeEvent) {
      this.rootBoundary.rootTarget = this.renderer.lastObjectRendered;
      if (this.supportsTouchEvents && nativeEvent.pointerType === "touch") return;
      var normalizedEvents = this.normalizeToPointerData(nativeEvent);
      for (var i = 0, j = normalizedEvents.length; i < j; i++) {
        var event = this.bootstrapEvent(this.rootPointerEvent, normalizedEvents[i]);
        this.rootBoundary.mapEvent(event);
      }
      this.setCursor(this.rootBoundary.cursor);
    }
  }, {
    key: "onWheel",
    value: function onWheel(nativeEvent) {
      var wheelEvent = this.normalizeWheelEvent(nativeEvent);
      this.rootBoundary.rootTarget = this.renderer.lastObjectRendered;
      this.rootBoundary.mapEvent(wheelEvent);
    }
  }, {
    key: "setTargetElement",
    value: function setTargetElement(element) {
      this.removeEvents();
      this.domElement = element;
      this.addEvents();
    }
  }, {
    key: "addEvents",
    value: function addEvents() {
      if (this.eventsAdded || !this.domElement) {
        return;
      }
      var style = this.domElement.style;
      if (style) {
        if (globalThis.navigator.msPointerEnabled) {
          style.msContentZooming = "none";
          style.msTouchAction = "none";
        } else if (this.supportsPointerEvents) {
          style.touchAction = "none";
        }
      }
      if (this.supportsPointerEvents) {
        globalThis.document.addEventListener("pointermove", this.onPointerMove, true);
        this.domElement.addEventListener("pointerdown", this.onPointerDown, true);
        this.domElement.addEventListener("pointerleave", this.onPointerOverOut, true);
        this.domElement.addEventListener("pointerover", this.onPointerOverOut, true);
        globalThis.addEventListener("pointerup", this.onPointerUp, true);
      } else {
        globalThis.document.addEventListener("mousemove", this.onPointerMove, true);
        this.domElement.addEventListener("mousedown", this.onPointerDown, true);
        this.domElement.addEventListener("mouseout", this.onPointerOverOut, true);
        this.domElement.addEventListener("mouseover", this.onPointerOverOut, true);
        globalThis.addEventListener("mouseup", this.onPointerUp, true);
      }
      if (this.supportsTouchEvents) {
        this.domElement.addEventListener("touchstart", this.onPointerDown, true);
        this.domElement.addEventListener("touchend", this.onPointerUp, true);
        this.domElement.addEventListener("touchmove", this.onPointerMove, true);
      }
      this.domElement.addEventListener("wheel", this.onWheel, {
        passive: true,
        capture: true
      });
      this.eventsAdded = true;
    }
  }, {
    key: "removeEvents",
    value: function removeEvents() {
      if (!this.eventsAdded || !this.domElement) {
        return;
      }
      var style = this.domElement.style;
      if (globalThis.navigator.msPointerEnabled) {
        style.msContentZooming = "";
        style.msTouchAction = "";
      } else if (this.supportsPointerEvents) {
        style.touchAction = "";
      }
      if (this.supportsPointerEvents) {
        globalThis.document.removeEventListener("pointermove", this.onPointerMove, true);
        this.domElement.removeEventListener("pointerdown", this.onPointerDown, true);
        this.domElement.removeEventListener("pointerleave", this.onPointerOverOut, true);
        this.domElement.removeEventListener("pointerover", this.onPointerOverOut, true);
        globalThis.removeEventListener("pointerup", this.onPointerUp, true);
      } else {
        globalThis.document.removeEventListener("mousemove", this.onPointerMove, true);
        this.domElement.removeEventListener("mousedown", this.onPointerDown, true);
        this.domElement.removeEventListener("mouseout", this.onPointerOverOut, true);
        this.domElement.removeEventListener("mouseover", this.onPointerOverOut, true);
        globalThis.removeEventListener("mouseup", this.onPointerUp, true);
      }
      if (this.supportsTouchEvents) {
        this.domElement.removeEventListener("touchstart", this.onPointerDown, true);
        this.domElement.removeEventListener("touchend", this.onPointerUp, true);
        this.domElement.removeEventListener("touchmove", this.onPointerMove, true);
      }
      this.domElement.removeEventListener("wheel", this.onWheel, true);
      this.domElement = null;
      this.eventsAdded = false;
    }
  }, {
    key: "mapPositionToPoint",
    value: function mapPositionToPoint(point, x, y) {
      var rect;
      if (!this.domElement.parentElement) {
        rect = {
          x: 0,
          y: 0,
          width: this.domElement.width,
          height: this.domElement.height,
          left: 0,
          top: 0
        };
      } else {
        rect = this.domElement.getBoundingClientRect();
      }
      var resolutionMultiplier = 1 / this.resolution;
      point.x = (x - rect.left) * (this.domElement.width / rect.width) * resolutionMultiplier;
      point.y = (y - rect.top) * (this.domElement.height / rect.height) * resolutionMultiplier;
    }
  }, {
    key: "normalizeToPointerData",
    value: function normalizeToPointerData(event) {
      var normalizedEvents = [];
      if (this.supportsTouchEvents && event instanceof TouchEvent) {
        for (var i = 0, li = event.changedTouches.length; i < li; i++) {
          var touch = event.changedTouches[i];
          if (typeof touch.button === "undefined") touch.button = 0;
          if (typeof touch.buttons === "undefined") touch.buttons = 1;
          if (typeof touch.isPrimary === "undefined") {
            touch.isPrimary = event.touches.length === 1 && event.type === "touchstart";
          }
          if (typeof touch.width === "undefined") touch.width = touch.radiusX || 1;
          if (typeof touch.height === "undefined") touch.height = touch.radiusY || 1;
          if (typeof touch.tiltX === "undefined") touch.tiltX = 0;
          if (typeof touch.tiltY === "undefined") touch.tiltY = 0;
          if (typeof touch.pointerType === "undefined") touch.pointerType = "touch";
          if (typeof touch.pointerId === "undefined") touch.pointerId = touch.identifier || 0;
          if (typeof touch.pressure === "undefined") touch.pressure = touch.force || 0.5;
          if (typeof touch.twist === "undefined") touch.twist = 0;
          if (typeof touch.tangentialPressure === "undefined") touch.tangentialPressure = 0;
          if (typeof touch.layerX === "undefined") touch.layerX = touch.offsetX = touch.clientX;
          if (typeof touch.layerY === "undefined") touch.layerY = touch.offsetY = touch.clientY;
          touch.isNormalized = true;
          touch.type = event.type;
          normalizedEvents.push(touch);
        }
      } else if (!globalThis.MouseEvent || event instanceof MouseEvent && (!this.supportsPointerEvents || !(event instanceof globalThis.PointerEvent))) {
        var tempEvent = event;
        if (typeof tempEvent.isPrimary === "undefined") tempEvent.isPrimary = true;
        if (typeof tempEvent.width === "undefined") tempEvent.width = 1;
        if (typeof tempEvent.height === "undefined") tempEvent.height = 1;
        if (typeof tempEvent.tiltX === "undefined") tempEvent.tiltX = 0;
        if (typeof tempEvent.tiltY === "undefined") tempEvent.tiltY = 0;
        if (typeof tempEvent.pointerType === "undefined") tempEvent.pointerType = "mouse";
        if (typeof tempEvent.pointerId === "undefined") tempEvent.pointerId = MOUSE_POINTER_ID;
        if (typeof tempEvent.pressure === "undefined") tempEvent.pressure = 0.5;
        if (typeof tempEvent.twist === "undefined") tempEvent.twist = 0;
        if (typeof tempEvent.tangentialPressure === "undefined") tempEvent.tangentialPressure = 0;
        tempEvent.isNormalized = true;
        normalizedEvents.push(tempEvent);
      } else {
        normalizedEvents.push(event);
      }
      return normalizedEvents;
    }
  }, {
    key: "normalizeWheelEvent",
    value: function normalizeWheelEvent(nativeEvent) {
      var event = this.rootWheelEvent;
      this.transferMouseData(event, nativeEvent);
      event.deltaMode = nativeEvent.deltaMode;
      event.deltaX = nativeEvent.deltaX;
      event.deltaY = nativeEvent.deltaY;
      event.deltaZ = nativeEvent.deltaZ;
      this.mapPositionToPoint(event.screen, nativeEvent.clientX, nativeEvent.clientY);
      event.global.copyFrom(event.screen);
      event.offset.copyFrom(event.screen);
      event.nativeEvent = nativeEvent;
      event.type = nativeEvent.type;
      return event;
    }
  }, {
    key: "bootstrapEvent",
    value: function bootstrapEvent(event, nativeEvent) {
      event.originalEvent = null;
      event.nativeEvent = nativeEvent;
      event.pointerId = nativeEvent.pointerId;
      event.width = nativeEvent.width;
      event.height = nativeEvent.height;
      event.isPrimary = nativeEvent.isPrimary;
      event.pointerType = nativeEvent.pointerType;
      event.pressure = nativeEvent.pressure;
      event.tangentialPressure = nativeEvent.tangentialPressure;
      event.tiltX = nativeEvent.tiltX;
      event.tiltY = nativeEvent.tiltY;
      event.twist = nativeEvent.twist;
      this.transferMouseData(event, nativeEvent);
      this.mapPositionToPoint(event.screen, nativeEvent.clientX, nativeEvent.clientY);
      event.global.copyFrom(event.screen);
      event.offset.copyFrom(event.screen);
      event.isTrusted = nativeEvent.isTrusted;
      if (event.type === "pointerleave") {
        event.type = "pointerout";
      }
      if (event.type.startsWith("mouse")) {
        event.type = event.type.replace("mouse", "pointer");
      }
      if (event.type.startsWith("touch")) {
        event.type = TOUCH_TO_POINTER[event.type] || event.type;
      }
      return event;
    }
  }, {
    key: "transferMouseData",
    value: function transferMouseData(event, nativeEvent) {
      event.isTrusted = nativeEvent.isTrusted;
      event.srcElement = nativeEvent.srcElement;
      event.timeStamp = performance.now();
      event.type = nativeEvent.type;
      event.altKey = nativeEvent.altKey;
      event.button = nativeEvent.button;
      event.buttons = nativeEvent.buttons;
      event.client.x = nativeEvent.clientX;
      event.client.y = nativeEvent.clientY;
      event.ctrlKey = nativeEvent.ctrlKey;
      event.metaKey = nativeEvent.metaKey;
      event.movement.x = nativeEvent.movementX;
      event.movement.y = nativeEvent.movementY;
      event.page.x = nativeEvent.pageX;
      event.page.y = nativeEvent.pageY;
      event.relatedTarget = null;
      event.shiftKey = nativeEvent.shiftKey;
    }
  }]);
  return EventSystem;
}();
EventSystem.extension = {
  name: "events",
  type: [ExtensionType.RendererSystem, ExtensionType.CanvasRendererSystem]
};
extensions.add(EventSystem);
export { EventSystem };